import { validatePhoneNumber } from '@massilia/queries';
import { format } from 'date-fns';
import { z } from 'zod';
import { firstNameSchema, lastNameSchema } from '../name.schema';
// =====================================================================
// BASE VALIDATION SCHEMAS
// Shared primitives and reusable validators with French error messages
// =====================================================================
// ---- Option Validation ----
const optionSchema = z.object({
    label: z.string().min(1),
    value: z.string().min(1),
});
// ---- Travel Class ----
export const travelClassTypes = {
    business: 'BUSINESS',
    economy: 'ECONOMY',
    premium: 'PREMIUM',
};
export const travelClassSchema = z.enum(['ECONOMY', 'PREMIUM', 'BUSINESS']);
// ---- Gender ----
export const genderTypes = {
    female: 'FEMALE',
    male: 'MALE',
};
const genderSchema = z.enum(['FEMALE', 'MALE']);
// ---- Tourism Types ----
export const tourismTypes = {
    CRUISE: 'CRUISE',
    STAY: 'STAY',
    TOUR: 'TOUR',
};
export const tourismSchema = z.enum(['CRUISE', 'STAY', 'TOUR']);
// ---- Pension Types ----
export const pensionTypes = {
    ALL_INCLUSIVE: 'ALL_INCLUSIVE',
    BREAKFAST: 'BREAKFAST',
    FULL_BOARD: 'FULL_BOARD',
    HALF_BOARD: 'HALF_BOARD',
    ROOM_ONLY: 'ROOM_ONLY',
};
export const pensionSchema = z.enum([
    'ALL_INCLUSIVE',
    'BREAKFAST',
    'FULL_BOARD',
    'HALF_BOARD',
    'ROOM_ONLY',
]);
// ---- Contact Validation ----
export const contactEmailSchema = z.string().min(1, 'Ce champ est requis').email('Email invalide');
export const contactPhoneSchema = z
    .string()
    .min(1, 'Numéro de téléphone requis')
    .refine((value) => validatePhoneNumber(value) === undefined, {
    message: 'Numéro de téléphone invalide',
});
// ---- Client Name Validation ----
export const clientFirstNameSchema = firstNameSchema;
export const clientLastNameSchema = lastNameSchema;
// ---- Airport Validation ----
// Includes optional category field for AirportAutocompleteField compatibility
export const airportOptionSchema = z
    .object({
    category: z.enum(['airport', 'city']).optional(),
    label: z.string(),
    value: z.string(),
})
    .refine((data) => data.value !== '', {
    message: 'Veuillez s\u00e9lectionner un a\u00e9roport',
});
export const DEFAULT_OPTION = { label: '', value: '' };
export const EMPTY_AIRPORT_OPTION = DEFAULT_OPTION;
// ---- Passenger Validation ----
const nationalityOptionSchema = optionSchema.refine((data) => data.value !== '', {
    message: 'Nationalité requise',
});
export const newPassengerSchema = z
    .object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    /**
     * Marqueur INTERNE au formulaire : id du passager enregistré dont cette ligne a
     * été préremplie. Jamais envoyé au serveur — au submit, les lignes portant ce
     * marqueur sont routées vers `existingPassengerIds` (lien, pas de doublon), et le
     * `.transform` ci-dessous le retire des lignes réellement nouvelles.
     */
    savedPassengerId: z.string().optional(),
    birthDate: z.string().min(1, 'Date de naissance requise'),
    gender: genderSchema,
    nationality: nationalityOptionSchema,
})
    // `savedPassengerId` est volontairement omis : marqueur interne, jamais envoyé.
    .transform((data) => ({
    firstName: data.firstName,
    lastName: data.lastName,
    birthDate: data.birthDate,
    gender: data.gender,
    nationality: data.nationality.value,
}));
/**
 * Passenger count validation check function.
 * Validates that total passengers (existing + new) is between 1 and 9.
 * Use with z.refine() on schemas that have existingPassengerIds and newPassengers fields.
 */
export function checkPassengerCount(data) {
    const existingCount = data.existingPassengerIds?.length ?? 0;
    const newCount = data.newPassengers?.length ?? 0;
    const total = existingCount + newCount;
    return total >= 1 && total <= 9;
}
/** Error message for passenger count validation */
export const PASSENGER_COUNT_ERROR = {
    message: 'Au moins 1 passager requis, maximum 9 passagers',
    path: ['newPassengers'],
};
// ---- Flexibility Validation ----
export const flexibilityDaysSchema = z.coerce
    .number()
    .int()
    .min(0, 'Doit \u00eatre positif ou z\u00e9ro');
// ---- Date Validation ----
export const futureDateSchema = z
    .string()
    .min(1, 'Date requise')
    .refine((dateStr) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return dateStr >= today;
}, {
    message: 'La date doit \u00eatre dans le futur',
});
